import React from 'react';
import { DateTime } from 'luxon';
import { isNullOrUndefined } from 'ontraccr-common/lib/Common';

import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import { getNextPayPeriodStartDate } from '../payroll';
import { overtimeRows } from '../../settings/TimeTrackingSettings/timeTrackingConstants';

/**
 * Keys that affect hour partitioning — changes defer effective date to the next pay period
 * (unless an immediate exception applies).
 */
export const PARTITIONING_OT_SETTING_KEYS = [
  'enableManualOT',
  'manualOTModifier',
  'weeklyOvertime',
  'workweekStartDay',
  'dailyOvertime',
  'dailyDouble',
  'saturdayOvertime',
  'sundayOvertime',
];

/**
 * All OT-rule keys tracked for save confirmation, versioning, and history.
 * Cost-only / modal keys version with immediate effective date when changed alone.
 */
export const OT_SETTING_KEYS = [
  ...PARTITIONING_OT_SETTING_KEYS,
  'saturdayPay',
  'sundayPay',
  'otEligibilitySelectionEnabled',
];

const BOOLEAN_OT_SETTING_KEYS = new Set([
  'enableManualOT',
  'otEligibilitySelectionEnabled',
]);

const normalizeOvertimeSettingValue = (key, value) => {
  if (BOOLEAN_OT_SETTING_KEYS.has(key)) {
    if (isNullOrUndefined(value)) return null;
    return value;
  }
  if (isNullOrUndefined(value) || value === false) return null;
  if (key === 'workweekStartDay' && !value) return 7;
  return value;
};

export const pickOvertimeSettings = (settings = {}) => {
  const picked = {};
  OT_SETTING_KEYS.forEach((key) => {
    picked[key] = normalizeOvertimeSettingValue(key, settings[key]);
  });
  return picked;
};

/**
 * Runtime GETs return currently-applied OT keys. When a scheduled version is pending,
 * the Overtime settings form should edit the latest (scheduled) snapshot instead.
 */
export const getLatestSavedOvertimeSettings = (
  appliedSettings = {},
  versionHistory = {},
) => {
  const { status, versions = [] } = versionHistory;
  if (status !== 'pending') return appliedSettings;
  const latest = versions.find((version) => version.isLatest) || versions[0];
  if (!latest?.settings) return appliedSettings;
  return {
    ...appliedSettings,
    ...latest.settings,
  };
};

const hasPartitioningOvertimeSettingsChange = (savedSettings = {}, draftSettings = {}) => {
  const saved = pickOvertimeSettings(savedSettings);
  const draft = pickOvertimeSettings(draftSettings);
  return PARTITIONING_OT_SETTING_KEYS.some((key) => saved[key] !== draft[key]);
};

export const hasOvertimeSettingsChanges = (savedSettings = {}, draftSettings = {}) => {
  const saved = pickOvertimeSettings(savedSettings);
  const draft = pickOvertimeSettings(draftSettings);
  return OT_SETTING_KEYS.some((key) => saved[key] !== draft[key]);
};

export const getOvertimeSettingsDiff = (savedSettings = {}, draftSettings = {}) => {
  const diff = {};
  OT_SETTING_KEYS.forEach((key) => {
    const savedVal = normalizeOvertimeSettingValue(key, savedSettings[key]);
    const draftVal = normalizeOvertimeSettingValue(key, draftSettings[key]);
    if (savedVal !== draftVal) {
      diff[key] = draftSettings[key];
    }
  });
  return diff;
};

/**
 * Immediate effective date when:
 * - already on manual and staying on manual, or
 * - on automatic and only non-partitioning keys changed (e.g. saturdayPay).
 */
export const usesImmediateOvertimeSettingsEffectiveDate = (
  savedSettings = {},
  draftSettings = {},
) => {
  if (savedSettings.enableManualOT && draftSettings.enableManualOT) return true;
  if (!savedSettings.enableManualOT && !draftSettings.enableManualOT
    && !hasPartitioningOvertimeSettingsChange(savedSettings, draftSettings)) {
    return true;
  }
  return false;
};

export const getOvertimeSettingsEffectiveDate = (
  companySettings = {},
  savedSettings = {},
  draftSettings = {},
) => {
  if (usesImmediateOvertimeSettingsEffectiveDate(savedSettings, draftSettings)) {
    return DateTime.local().startOf('day');
  }
  return getNextPayPeriodStartDate(companySettings);
};

export const formatOvertimeSettingsEffectiveDate = (effectiveDate) => {
  if (!effectiveDate?.isValid) return '';
  return effectiveDate.toLocaleString(DateTime.DATE_MED);
};

export const hasOTSettingsVersionHistory = (versionHistory = []) => versionHistory.length > 0;

const getPendingScheduledVersion = (versionHistory = []) => {
  const today = DateTime.local().startOf('day').toISODate();
  return versionHistory.find((v) => v.effectiveDate > today) ?? null;
};

export const getOvertimeSettingsSaveConfirmationMessage = (
  companySettings = {},
  savedSettings = {},
  draftSettings = {},
  { versionHistory = [] } = {},
) => {
  const pendingVersion = getPendingScheduledVersion(versionHistory);
  const isImmediate = usesImmediateOvertimeSettingsEffectiveDate(savedSettings, draftSettings);

  if (!hasOTSettingsVersionHistory(versionHistory) || (isImmediate && !pendingVersion)) {
    return 'These settings will apply immediately.';
  }

  // When an immediate-effective save happens alongside a pending scheduled version, the change
  // folds into the scheduled version rather than creating a new today-dated row.
  if (isImmediate && pendingVersion) {
    const formattedDate = formatOvertimeSettingsEffectiveDate(
      DateTime.fromISO(pendingVersion.effectiveDate),
    );
    return `These settings will replace the currently scheduled change and take effect ${formattedDate}.`;
  }

  const effectiveDate = getOvertimeSettingsEffectiveDate(
    companySettings,
    savedSettings,
    draftSettings,
  );
  const formattedDate = formatOvertimeSettingsEffectiveDate(effectiveDate);

  const replacingScheduled = !!pendingVersion;
  const replacingPrefix = replacingScheduled ? 'This will replace the currently scheduled change. ' : '';

  if (!savedSettings.enableManualOT && draftSettings.enableManualOT) {
    return `${replacingPrefix}Switching to manual overtime will take effect for the pay period starting ${formattedDate}. Automatic overtime rules will continue to apply until then.`;
  }

  if (savedSettings.enableManualOT && !draftSettings.enableManualOT) {
    return `${replacingPrefix}Switching to automatic overtime will take effect for the pay period starting ${formattedDate}. Manual overtime rules will continue to apply until then.`;
  }

  return `${replacingPrefix}These changes will take effect for the pay period starting ${formattedDate}.`;
};

const OVERTIME_SETTINGS_SAVE_CANCEL_NOTE = 'Cancelling will not reset values to their saved state. '
  + 'Refresh the page to discard unsaved changes.';

export const confirmOvertimeSettingsSave = ({
  companySettings,
  savedSettings,
  draftSettings,
  versionHistory = [],
  onConfirm,
}) => {
  CustomConfirmModal({
    title: 'Save overtime settings?',
    content: (
      <div>
        <p>{getOvertimeSettingsSaveConfirmationMessage(
          companySettings,
          savedSettings,
          draftSettings,
          { versionHistory },
        )}
        </p>
        <p className="overtime-settings-save-cancel-note">{OVERTIME_SETTINGS_SAVE_CANCEL_NOTE}</p>
      </div>
    ),
    okText: 'Save',
    cancelText: 'Cancel',
    onOk: onConfirm,
  });
};

const OT_PROFILE_ASSIGNMENT_CONFIRMATION = {
  users: {
    title: 'Assign users to this profile?',
    content: 'This profile will take effect immediately for the selected users. '
      + 'Profile assignment is not tracked historically, so existing time entries may be '
      + 'recalculated using this profile\'s settings and overtime values may change.',
  },
  divisions: {
    title: 'Assign divisions to this profile?',
    content: 'This profile will take effect immediately for the selected divisions and their users. '
      + 'Profile assignment is not tracked historically, so existing time entries may be '
      + 'recalculated using this profile\'s settings and overtime values may change.',
  },
};

export const confirmOvertimeProfileAssignment = ({ type, onConfirm }) => {
  const { title, content } = OT_PROFILE_ASSIGNMENT_CONFIRMATION[type];
  CustomConfirmModal({
    title,
    content,
    okText: 'Assign',
    cancelText: 'Cancel',
    onOk: onConfirm,
  });
};

const WORKWEEK_DAY_LABELS = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const OT_TYPE_LABELS = {
  overtime: 'Overtime',
  double: 'Double Time',
};

const SETTING_LABELS = {
  enableManualOT: 'Overtime Mode',
  manualOTModifier: 'Overtime Pay Rate',
  ...overtimeRows.reduce((acc, row) => {
    acc[row.key] = row.title;
    return acc;
  }, {}),
};

const EMPTY_DISPLAY = '—';

export const formatOTSettingsHistoryDate = (dateISO) => {
  if (!dateISO) return '';
  const dt = DateTime.fromISO(dateISO);
  if (!dt.isValid) return dateISO;
  return dt.toLocaleString(DateTime.DATE_MED);
};

export const formatOTSettingsHistoryValue = (key, value) => {
  if (value === undefined || value === null || value === false) {
    if (key === 'enableManualOT' && value === false) return 'Automatic';
    if (key === 'otEligibilitySelectionEnabled' && value === false) return 'Off';
    return EMPTY_DISPLAY;
  }
  if (key === 'enableManualOT') {
    return value ? 'Manual' : 'Automatic';
  }
  if (key === 'otEligibilitySelectionEnabled') {
    return value ? 'On' : 'Off';
  }
  if (key === 'manualOTModifier') {
    return `wage x ${value}`;
  }
  if (key === 'saturdayPay' || key === 'sundayPay') {
    return `wage x ${value}`;
  }
  if (key === 'workweekStartDay') {
    return WORKWEEK_DAY_LABELS[value] ?? String(value);
  }
  if (key === 'saturdayOvertime' || key === 'sundayOvertime') {
    return OT_TYPE_LABELS[value] ?? String(value);
  }
  if (key === 'weeklyOvertime' || key === 'dailyOvertime' || key === 'dailyDouble') {
    return `${value} hours`;
  }
  return String(value);
};

const MANUAL_OT_HISTORY_KEYS = [
  'enableManualOT',
  'manualOTModifier',
];

const getOTSettingsHistoryKeys = (settings = {}) => {
  if (settings.enableManualOT) {
    return MANUAL_OT_HISTORY_KEYS;
  }
  return OT_SETTING_KEYS.filter((key) => key !== 'manualOTModifier');
};

export const formatOTSettingsHistoryEntries = (settings = {}) => {
  const keys = getOTSettingsHistoryKeys(settings);
  return keys.map((key) => {
    const formatted = formatOTSettingsHistoryValue(key, settings[key]);
    return {
      key,
      label: SETTING_LABELS[key] ?? key,
      value: formatted,
      isEmpty: formatted === EMPTY_DISPLAY,
    };
  });
};
